import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { paymentsApi, studentsApi, studentFeesApi } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Receipt, MagnifyingGlass, Trash, UsersThree } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Payments = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    student_id: '',
    payment_type: 'tuition',
    amount: '',
    payment_method: 'cash',
    description: '',
    discount_percent: 0
  });

  // Live fee breakdown for the selected student (for cashier visibility on discounts)
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [transportZones, setTransportZones] = useState([]);
  const [activatingService, setActivatingService] = useState(false);

  // Bulk canteen/transport per class (admin)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkForm, setBulkForm] = useState({ class_name: '', service: 'canteen', action: 'enable', transport_zone: '' });
  const [bulkSummary, setBulkSummary] = useState(null);
  const [bulkSummaryLoading, setBulkSummaryLoading] = useState(false);

  const reloadBreakdown = () => {
    if (!formData.student_id) return;
    setBreakdownLoading(true);
    studentFeesApi.get(formData.student_id)
      .then(({ data }) => setFeeBreakdown(data))
      .catch(() => setFeeBreakdown(null))
      .finally(() => setBreakdownLoading(false));
  };

  useEffect(() => {
    if (!dialogOpen || !formData.student_id) {
      setFeeBreakdown(null);
      return;
    }
    let cancelled = false;
    setBreakdownLoading(true);
    studentFeesApi.get(formData.student_id)
      .then(({ data }) => { if (!cancelled) setFeeBreakdown(data); })
      .catch(() => { if (!cancelled) setFeeBreakdown(null); })
      .finally(() => { if (!cancelled) setBreakdownLoading(false); });
    return () => { cancelled = true; };
  }, [dialogOpen, formData.student_id]);

  // Load transport zones once (needed for the "Activer maintenant" transport flow)
  useEffect(() => {
    api.get('/fee-config').then(({ data }) => {
      setTransportZones(data?.transport_zones || []);
    }).catch(() => setTransportZones([]));
  }, []);

  // Fetch current canteen/transport counts for the chosen class (recap before applying)
  useEffect(() => {
    if (!bulkOpen || !bulkForm.class_name) { setBulkSummary(null); return; }
    let cancelled = false;
    setBulkSummaryLoading(true);
    studentFeesApi.classSummary(bulkForm.class_name)
      .then(({ data }) => { if (!cancelled) setBulkSummary(data); })
      .catch(() => { if (!cancelled) setBulkSummary(null); })
      .finally(() => { if (!cancelled) setBulkSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [bulkOpen, bulkForm.class_name]);

  const handleBulkService = async () => {
    const { class_name, service, action, transport_zone } = bulkForm;
    if (!class_name) {
      toast.error(language === 'fr' ? 'Sélectionnez une classe' : 'Select a class');
      return;
    }
    const enabled = action === 'enable';
    if (service === 'transport' && enabled && !transport_zone) {
      toast.error(language === 'fr' ? 'Sélectionnez une zone de transport' : 'Select a transport zone');
      return;
    }
    // Confirm when a bulk action will change many students at once.
    const svcSummary = bulkSummary?.[service];
    const impacted = svcSummary ? (enabled ? svcSummary.off : svcSummary.on) : null;
    const CONFIRM_THRESHOLD = 10;
    if (impacted != null && impacted >= CONFIRM_THRESHOLD) {
      const svcName = service === 'canteen'
        ? (language === 'fr' ? 'la cantine' : 'canteen')
        : (language === 'fr' ? 'le transport' : 'transport');
      const act = enabled ? (language === 'fr' ? 'activer' : 'enable') : (language === 'fr' ? 'désactiver' : 'disable');
      const ok = window.confirm(language === 'fr'
        ? `Vous allez ${act} ${svcName} pour ${impacted} élève(s) de ${class_name}. Confirmer ?`
        : `This will ${act} ${svcName} for ${impacted} students in ${class_name}. Continue?`);
      if (!ok) return;
    }
    setBulkSubmitting(true);
    try {
      const payload = { class_name, override_existing: true };
      if (service === 'canteen') payload.has_canteen = enabled;
      else { payload.has_transport = enabled; if (enabled) payload.transport_zone = transport_zone; }
      const { data } = await studentFeesApi.bulkEnableServices(payload);
      const svc = service === 'canteen'
        ? (language === 'fr' ? 'Cantine' : 'Canteen')
        : (language === 'fr' ? 'Transport' : 'Transport');
      const verb = enabled ? (language === 'fr' ? 'activé(e)' : 'enabled') : (language === 'fr' ? 'désactivé(e)' : 'disabled');
      toast.success(language === 'fr'
        ? `${svc} ${verb} — ${data.updated}/${data.total_students} élève(s) de ${class_name}`
        : `${svc} ${verb} — ${data.updated}/${data.total_students} students in ${class_name}`);
      setBulkOpen(false);
      reloadBreakdown();
      setBulkSummary(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleActivateCanteen = async () => {
    if (!isAdmin) {
      toast.error(language === 'fr' ? 'Action réservée à l\'administrateur' : 'Admin only');
      return;
    }
    setActivatingService(true);
    try {
      await studentFeesApi.toggleService(formData.student_id, { service: 'canteen', enabled: true });
      toast.success(language === 'fr' ? 'Cantine activée pour cet élève' : 'Canteen enabled for this student');
      reloadBreakdown();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally {
      setActivatingService(false);
    }
  };

  const handleActivateTransport = async (zoneKey) => {
    if (!isAdmin) {
      toast.error(language === 'fr' ? 'Action réservée à l\'administrateur' : 'Admin only');
      return;
    }
    if (!zoneKey) {
      toast.error(language === 'fr' ? 'Sélectionnez une zone de transport' : 'Select a transport zone');
      return;
    }
    setActivatingService(true);
    try {
      await studentFeesApi.toggleService(formData.student_id, {
        service: 'transport', enabled: true, transport_zone: zoneKey,
      });
      toast.success(language === 'fr' ? 'Transport activé pour cet élève' : 'Transport enabled for this student');
      reloadBreakdown();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally {
      setActivatingService(false);
    }
  };

  const handleDeactivateService = async (service) => {
    if (!isAdmin) {
      toast.error(language === 'fr' ? 'Action réservée à l\'administrateur' : 'Admin only');
      return;
    }
    const label = service === 'canteen'
      ? (language === 'fr' ? 'la cantine' : 'the canteen')
      : (language === 'fr' ? 'le transport' : 'transport');
    if (!window.confirm(language === 'fr'
      ? `Désactiver ${label} pour cet élève ? Le solde de ce service ne sera plus dû.`
      : `Disable ${label} for this student?`)) return;
    setActivatingService(true);
    try {
      await studentFeesApi.toggleService(formData.student_id, { service, enabled: false });
      toast.success(service === 'canteen'
        ? (language === 'fr' ? 'Cantine désactivée pour cet élève' : 'Canteen disabled for this student')
        : (language === 'fr' ? 'Transport désactivé pour cet élève' : 'Transport disabled for this student'));
      reloadBreakdown();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally {
      setActivatingService(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [paymentsRes, studentsRes] = await Promise.all([
        paymentsApi.getAll(),
        studentsApi.getAll()
      ]);
      setPayments(paymentsRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await paymentsApi.create({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success(t('success'));
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const resetForm = () => {
    setFormData({
      student_id: '',
      payment_type: 'tuition',
      amount: '',
      payment_method: 'cash',
      description: '',
      discount_percent: 0
    });
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await paymentsApi.getReceipt(paymentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await paymentsApi.delete(deleteTarget.id);
      toast.success(
        language === 'fr'
          ? `Paiement ${deleteTarget.receipt_number} supprimé`
          : `Payment ${deleteTarget.receipt_number} deleted`
      );
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const paymentTypes = [
    { value: 'tuition', label: t('tuition') },
    { value: 'registration', label: language === 'en' ? 'Registration' : 'Inscription' },
    { value: 'reregistration', label: language === 'en' ? 'Re-registration' : 'Ré-inscription' },
    { value: 'canteen', label: t('canteen') },
    { value: 'transport', label: t('transport') },
    { value: 'extra', label: t('extra') }
  ];

  // Hide the inapplicable enrolment fee in the add-payment dialog:
  // returning students never owe *registration*, new students never owe *re-registration*.
  const dialogPaymentTypes = paymentTypes.filter(pt => {
    if (pt.value === 'registration' && feeBreakdown && feeBreakdown.registration?.applicable === false) return false;
    if (pt.value === 'reregistration' && feeBreakdown && feeBreakdown.reregistration?.applicable === false) return false;
    return true;
  });


  const paymentMethods = [
    { value: 'cash', label: t('cash') },
    { value: 'cheque', label: t('cheque') },
    { value: 'transfer', label: t('transfer') },
    { value: 'deposit', label: t('deposit') }
  ];

  const filteredPayments = payments.filter(payment => {
    const matchesType = filterType === 'all' || payment.payment_type === filterType;
    const matchesSearch = payment.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('payments')}</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => { setBulkForm({ class_name: '', service: 'canteen', action: 'enable', transport_zone: '' }); setBulkOpen(true); }}
              className="border-[#E2E8F0] text-[#0F172A]"
              data-testid="bulk-service-btn"
            >
              <UsersThree size={18} className="mr-2" />
              {language === 'fr' ? 'Cantine/Transport par classe' : 'Canteen/Transport by class'}
            </Button>
          )}
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#0052FF] hover:bg-[#0043D1]"
            data-testid="add-payment-btn"
          >
            <Plus size={18} className="mr-2" />
            {t('addPayment')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                <Input
                  placeholder={t('search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-[#E2E8F0]"
                  data-testid="search-payment"
                />
              </div>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px] border-[#E2E8F0]" data-testid="filter-payment-type">
                <SelectValue placeholder={t('paymentType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {paymentTypes.map(pt => (
                  <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('date')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('receipt')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('students')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('paymentType')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('amount')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('paymentMethod')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF]" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#64748B]">
                    {t('noData')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]" data-testid={`payment-row-${payment.id}`}>
                    <TableCell className="font-manrope">{payment.created_at?.slice(0, 10)}</TableCell>
                    <TableCell className="font-manrope font-mono text-sm">{payment.receipt_number}</TableCell>
                    <TableCell className="font-manrope">{payment.student_name}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium
                        ${payment.payment_type === 'tuition' ? 'bg-blue-100 text-blue-700' :
                          payment.payment_type === 'canteen' ? 'bg-green-100 text-green-700' :
                          payment.payment_type === 'transport' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-purple-100 text-purple-700'}`}
                      >
                        {t(payment.payment_type)}
                      </span>
                    </TableCell>
                    <TableCell className="font-manrope font-semibold">{formatCurrency(payment.amount)}</TableCell>
                    <TableCell className="font-manrope">{t(payment.payment_method)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadReceipt(payment.id)}
                        className="hover:bg-[#EFF6FF] text-[#0052FF]"
                        data-testid={`download-receipt-${payment.id}`}
                      >
                        <Receipt size={18} />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(payment)}
                          className="hover:bg-red-50 text-red-600"
                          data-testid={`delete-payment-${payment.id}`}
                          title={language === 'fr' ? 'Supprimer ce paiement' : 'Delete this payment'}
                        >
                          <Trash size={18} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{t('addPayment')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{t('students')}</Label>
              <Select 
                value={formData.student_id} 
                onValueChange={(v) => setFormData({ ...formData, student_id: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="payment-student">
                  <SelectValue placeholder={t('students')} />
                </SelectTrigger>
                <SelectContent>
                  {students.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name} - {s.class_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('paymentType')}</Label>
              <Select 
                value={formData.payment_type} 
                onValueChange={(v) => setFormData({ ...formData, payment_type: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="payment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dialogPaymentTypes.map(pt => (
                    <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Live fee breakdown — applies discount before payment validation */}
            {formData.student_id && (
              <div
                className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-1.5"
                data-testid="fee-breakdown-panel"
              >
                {breakdownLoading ? (
                  <p className="text-xs font-manrope text-[#64748B]">
                    {language === 'fr' ? 'Calcul des frais…' : 'Calculating fees…'}
                  </p>
                ) : feeBreakdown ? (() => {
                  const ptype = formData.payment_type;
                  const section = feeBreakdown[ptype] || {};
                  const isExtra = ptype === 'extra';
                  if (isExtra) {
                    return (
                      <p className="text-xs font-manrope text-[#64748B]">
                        {language === 'fr' ? 'Frais extra — montant libre.' : 'Extra fee — free amount.'}
                      </p>
                    );
                  }
                  // Service-disabled guards (canteen / transport)
                  if (ptype === 'canteen' && section.enabled === false) {
                    return (
                      <div className="space-y-2" data-testid="fb-canteen-disabled">
                        <p className="text-xs font-manrope text-amber-700">
                          {language === 'fr'
                            ? '⚠️ Cantine non activée pour cet élève.'
                            : '⚠️ Canteen not enabled for this student.'}
                        </p>
                        {isAdmin && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleActivateCanteen}
                            disabled={activatingService}
                            className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                            data-testid="activate-canteen-btn"
                          >
                            {activatingService
                              ? (language === 'fr' ? 'Activation…' : 'Enabling…')
                              : (language === 'fr' ? 'Activer maintenant' : 'Enable now')}
                          </Button>
                        )}
                      </div>
                    );
                  }
                  if (ptype === 'transport' && section.enabled === false) {
                    return (
                      <div className="space-y-2" data-testid="fb-transport-disabled">
                        <p className="text-xs font-manrope text-amber-700">
                          {language === 'fr'
                            ? '⚠️ Transport non activé pour cet élève.'
                            : '⚠️ Transport not enabled for this student.'}
                        </p>
                        {isAdmin && transportZones.length > 0 && (
                          <div className="flex gap-2 items-center">
                            <Select onValueChange={handleActivateTransport}>
                              <SelectTrigger
                                className="h-8 text-xs w-44"
                                data-testid="activate-transport-zone"
                              >
                                <SelectValue placeholder={language === 'fr' ? 'Choisir une zone…' : 'Pick a zone…'} />
                              </SelectTrigger>
                              <SelectContent>
                                {transportZones.map(z => (
                                  <SelectItem key={z.key} value={z.key}>
                                    {z.name} ({new Intl.NumberFormat('fr-FR').format(z.amount || 0)} FCFA)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {activatingService && (
                              <span className="text-xs text-[#64748B]">
                                {language === 'fr' ? 'Activation…' : 'Enabling…'}
                              </span>
                            )}
                          </div>
                        )}
                        {isAdmin && transportZones.length === 0 && (
                          <p className="text-xs text-[#64748B]">
                            {language === 'fr'
                              ? 'Aucune zone configurée. Allez dans Paramètres → Frais.'
                              : 'No zones configured. Go to Settings → Fees.'}
                          </p>
                        )}
                      </div>
                    );
                  }
                  // Registration / Re-registration not configured in settings
                  if ((ptype === 'registration' || ptype === 'reregistration') && (section.base_amount || 0) === 0) {
                    return (
                      <p className="text-xs font-manrope text-amber-700" data-testid={`fb-${ptype}-not-set`}>
                        {language === 'fr'
                          ? `⚠️ Frais de ${ptype === 'registration' ? "d'inscription" : 'réinscription'} non définis. Configurez-les dans Paramètres → Frais.`
                          : `⚠️ ${ptype === 'registration' ? 'Registration' : 'Re-registration'} fee not set. Configure it in Settings → Fees.`}
                      </p>
                    );
                  }
                  const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
                  const base = section.base_amount ?? 0;
                  const discountPct = section.discount_percent ?? 0;
                  const discounted = section.discounted_amount ?? base;
                  const paid = section.paid_amount ?? 0;
                  const balance = section.balance ?? Math.max(0, discounted - paid);
                  const zoneInfo = ptype === 'transport' && section.zone_name
                    ? ` — ${language === 'fr' ? 'Zone' : 'Zone'}: ${section.zone_name}`
                    : '';
                  return (
                    <>
                      {zoneInfo && (
                        <div className="text-xs font-manrope text-indigo-700 font-semibold pb-1">
                          {zoneInfo.trim()}
                        </div>
                      )}
                      <div className="flex justify-between text-xs font-manrope">
                        <span className="text-[#64748B]">{language === 'fr' ? 'Frais de base' : 'Base fee'}</span>
                        <span className="text-[#0F172A]" data-testid="fb-base">{fmt(base)} FCFA</span>
                      </div>
                      {discountPct > 0 && (
                        <div className="flex justify-between text-xs font-manrope">
                          <span className="text-amber-700">
                            {language === 'fr' ? 'Remise appliquée' : 'Discount applied'} ({discountPct}%)
                          </span>
                          <span className="text-amber-700" data-testid="fb-discount">
                            -{fmt(base - discounted)} FCFA
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-xs font-manrope font-semibold border-t border-indigo-200/60 pt-1.5">
                        <span className="text-[#0F172A]">
                          {language === 'fr' ? 'Frais nets à payer' : 'Net fee'}
                        </span>
                        <span className="text-indigo-700" data-testid="fb-net">{fmt(discounted)} FCFA</span>
                      </div>
                      <div className="flex justify-between text-xs font-manrope">
                        <span className="text-[#64748B]">{language === 'fr' ? 'Déjà payé' : 'Already paid'}</span>
                        <span className="text-emerald-700" data-testid="fb-paid">{fmt(paid)} FCFA</span>
                      </div>
                      <div className="flex justify-between text-xs font-manrope font-semibold">
                        <span className="text-[#0F172A]">{language === 'fr' ? 'Solde restant' : 'Remaining balance'}</span>
                        <span className="text-red-600" data-testid="fb-balance">{fmt(balance)} FCFA</span>
                      </div>
                      {isAdmin && (ptype === 'canteen' || ptype === 'transport') && (
                        <div className="pt-2 border-t border-indigo-200/60 mt-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeactivateService(ptype)}
                            disabled={activatingService}
                            className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`deactivate-${ptype}-btn`}
                          >
                            {activatingService
                              ? (language === 'fr' ? 'Mise à jour…' : 'Updating…')
                              : (language === 'fr'
                                  ? (ptype === 'canteen' ? 'Désactiver la cantine' : 'Désactiver le transport')
                                  : (ptype === 'canteen' ? 'Disable canteen' : 'Disable transport'))}
                          </Button>
                        </div>
                      )}
                    </>
                  );
                })() : (
                  <p className="text-xs font-manrope text-[#64748B]">
                    {language === 'fr' ? 'Impossible de charger les frais.' : 'Failed to load fees.'}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="font-manrope">{t('amount')} (FCFA)</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="payment-amount"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('paymentMethod')}</Label>
              <Select 
                value={formData.payment_method} 
                onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(pm => (
                    <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-[#0052FF] hover:bg-[#0043D1]" data-testid="save-payment-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk canteen/transport per class (admin) */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-service-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <UsersThree size={22} className="text-[#0052FF]" />
              {language === 'fr' ? 'Cantine / Transport par classe' : 'Canteen / Transport by class'}
            </DialogTitle>
            <DialogDescription className="font-manrope text-[#64748B]">
              {language === 'fr'
                ? 'Activez ou désactivez un service pour tous les élèves d\'une classe en une seule action.'
                : 'Enable or disable a service for every student of a class in one action.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Classe' : 'Class'}</Label>
              <Select value={bulkForm.class_name} onValueChange={(v) => setBulkForm({ ...bulkForm, class_name: v })}>
                <SelectTrigger className="border-[#E2E8F0]" data-testid="bulk-class-select">
                  <SelectValue placeholder={language === 'fr' ? 'Choisir une classe…' : 'Pick a class…'} />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set(students.map(s => s.class_id).filter(Boolean))].sort().map(cn => (
                    <SelectItem key={cn} value={cn}>{cn}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {bulkForm.class_name && (
              <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3" data-testid="bulk-class-summary">
                {bulkSummaryLoading ? (
                  <p className="text-xs text-[#64748B] font-manrope">{language === 'fr' ? 'Chargement du récap…' : 'Loading summary…'}</p>
                ) : bulkSummary ? (
                  <div className="space-y-1.5">
                    <p className="text-xs font-manrope text-[#64748B]">
                      {language === 'fr' ? 'État actuel' : 'Current state'} — {bulkSummary.total_students} {language === 'fr' ? 'élève(s)' : 'students'}
                    </p>
                    {['canteen', 'transport'].map(svc => {
                      const s = bulkSummary[svc] || { on: 0, off: 0 };
                      const label = svc === 'canteen' ? (language === 'fr' ? 'Cantine' : 'Canteen') : (language === 'fr' ? 'Transport' : 'Transport');
                      const active = bulkForm.service === svc;
                      return (
                        <div key={svc} className={`flex items-center justify-between text-xs font-manrope ${active ? 'font-semibold text-[#0F172A]' : 'text-[#94A3B8]'}`} data-testid={`bulk-summary-${svc}`}>
                          <span>{label}</span>
                          <span className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {s.on} {language === 'fr' ? 'activé' : 'on'}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[#94A3B8]">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-300" /> {s.off} {language === 'fr' ? 'inactif' : 'off'}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-[#94A3B8] font-manrope">{language === 'fr' ? 'Récap indisponible.' : 'Summary unavailable.'}</p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-manrope">{language === 'fr' ? 'Service' : 'Service'}</Label>
                <Select value={bulkForm.service} onValueChange={(v) => setBulkForm({ ...bulkForm, service: v, transport_zone: '' })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="bulk-service-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="canteen">{language === 'fr' ? 'Cantine' : 'Canteen'}</SelectItem>
                    <SelectItem value="transport">{language === 'fr' ? 'Transport' : 'Transport'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-manrope">{language === 'fr' ? 'Action' : 'Action'}</Label>
                <Select value={bulkForm.action} onValueChange={(v) => setBulkForm({ ...bulkForm, action: v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="bulk-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enable">{language === 'fr' ? 'Activer' : 'Enable'}</SelectItem>
                    <SelectItem value="disable">{language === 'fr' ? 'Désactiver' : 'Disable'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {bulkForm.service === 'transport' && bulkForm.action === 'enable' && (
              <div className="space-y-2">
                <Label className="font-manrope">{language === 'fr' ? 'Zone de transport' : 'Transport zone'}</Label>
                <Select value={bulkForm.transport_zone} onValueChange={(v) => setBulkForm({ ...bulkForm, transport_zone: v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="bulk-transport-zone">
                    <SelectValue placeholder={language === 'fr' ? 'Choisir une zone…' : 'Pick a zone…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {transportZones.map(z => (
                      <SelectItem key={z.key} value={z.key}>
                        {z.name} ({new Intl.NumberFormat('fr-FR').format(z.amount || 0)} FCFA)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {transportZones.length === 0 && (
                  <p className="text-xs text-amber-700">
                    {language === 'fr' ? 'Aucune zone configurée (Paramètres → Frais).' : 'No zones configured (Settings → Fees).'}
                  </p>
                )}
              </div>
            )}
            <p className="text-xs text-[#64748B]">
              {language === 'fr'
                ? 'Les réglages individuels existants seront écrasés pour toute la classe.'
                : 'Existing individual settings will be overwritten for the whole class.'}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkOpen(false)}>{t('cancel')}</Button>
            <Button
              type="button"
              onClick={handleBulkService}
              disabled={bulkSubmitting || !bulkForm.class_name}
              className="bg-[#0052FF] hover:bg-[#0043D1]"
              data-testid="bulk-service-submit"
            >
              {bulkSubmitting ? '…' : (() => {
                const svc = bulkSummary?.[bulkForm.service];
                const impacted = svc ? (bulkForm.action === 'enable' ? svc.off : svc.on) : null;
                const base = bulkForm.action === 'enable'
                  ? (language === 'fr' ? 'Activer' : 'Enable')
                  : (language === 'fr' ? 'Désactiver' : 'Disable');
                if (impacted == null) return language === 'fr' ? 'Appliquer' : 'Apply';
                return language === 'fr' ? `${base} pour ${impacted}` : `${base} for ${impacted}`;
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog (admin only) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-red-700 flex items-center gap-2">
              <Trash size={22} />
              {language === 'fr' ? 'Supprimer ce paiement ?' : 'Delete this payment?'}
            </DialogTitle>
            <DialogDescription className="font-manrope text-[#475569] pt-3 space-y-1">
              {deleteTarget && (
                <>
                  <div><strong>{language === 'fr' ? 'Élève' : 'Student'}:</strong> {deleteTarget.student_name}</div>
                  <div><strong>{language === 'fr' ? 'Type' : 'Type'}:</strong> {t(deleteTarget.payment_type)}</div>
                  <div><strong>{language === 'fr' ? 'Montant' : 'Amount'}:</strong> {new Intl.NumberFormat('fr-FR').format(deleteTarget.amount || 0)} FCFA</div>
                  <div><strong>{language === 'fr' ? 'N° Reçu' : 'Receipt'}:</strong> <span className="font-mono text-xs">{deleteTarget.receipt_number}</span></div>
                  <div className="text-red-600 font-semibold pt-2">
                    {language === 'fr'
                      ? '⚠️ Action irréversible. Le reçu PDF ne sera plus accessible.'
                      : '⚠️ Irreversible. The PDF receipt will no longer be accessible.'}
                  </div>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="delete-cancel-btn">
              {t('cancel')}
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-btn"
            >
              {deleting ? (language === 'fr' ? 'Suppression…' : 'Deleting…') : (language === 'fr' ? 'Supprimer' : 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
